/**
 * CustomerSubscriptionsAdmin – Admin view: pending / active subscriptions.
 * Approve / reject pending payments, view download history.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Clock, Crown, Eye, RefreshCw,
  BarChart2, Users, Download, Filter,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_CFG = {
  active:   { label: "Active",   cls: "bg-green-100 text-green-700" },
  pending:  { label: "Pending",  cls: "bg-yellow-100 text-yellow-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-600" },
  expired:  { label: "Expired",  cls: "bg-slate-100 text-slate-500" },
  cancelled:{ label: "Cancelled",cls: "bg-slate-100 text-slate-500" },
};

function SubDetailModal({ sub, onClose, onApprove, onReject }) {
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Subscription Detail</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><XCircle className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ["Customer",     sub.customer_name || sub.customer_email],
              ["Email",        sub.customer_email],
              ["Plan",         sub.plan_name],
              ["Status",       sub.status],
              ["Applied on",   fmtDateTime(sub.created_at)],
              ["Payment",      sub.payment_method?.replace("_"," ")],
            ].map(([l,v]) => (
              <div key={l}>
                <p className="text-xs text-slate-400 font-semibold">{l}</p>
                <p className="font-semibold text-slate-800 capitalize">{v || "—"}</p>
              </div>
            ))}
          </div>

          {sub.payment_screenshot_url && (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Payment Screenshot</p>
              <img src={sub.payment_screenshot_url} alt="Payment proof"
                className="w-full max-h-64 object-contain border border-slate-200 rounded-xl bg-slate-50" />
              <a href={sub.payment_screenshot_url} target="_blank" rel="noreferrer"
                className="text-xs text-orange-600 hover:underline mt-1 block">View full size ↗</a>
            </div>
          )}

          {sub.payment_notes && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-500 uppercase mb-1">Customer Notes</p>
              <p className="text-sm text-slate-700">{sub.payment_notes}</p>
            </div>
          )}

          {sub.plan_snapshot && (
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Plan Details</p>
              <div className="grid grid-cols-2 gap-1.5 text-xs text-slate-600">
                <span>Price: ₹{sub.plan_snapshot.price}</span>
                <span>Validity: {sub.plan_snapshot.validity_days} days</span>
                <span>Downloads: {sub.plan_snapshot.download_limit === -1 ? "Unlimited" : sub.plan_snapshot.download_limit}</span>
                <span>Billing: {sub.plan_snapshot.billing}</span>
              </div>
            </div>
          )}

          {sub.status === "pending" && (
            <div className="space-y-3 pt-2">
              {showReject ? (
                <div className="space-y-2">
                  <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    placeholder="Reason for rejection (optional)" rows={2}
                    className="w-full text-sm border border-red-200 rounded-xl px-3 py-2 focus:outline-none focus:border-red-400 resize-none" />
                  <div className="flex gap-2">
                    <button onClick={() => setShowReject(false)}
                      className="flex-1 border border-slate-200 text-slate-600 font-semibold py-2 rounded-xl text-sm">Cancel</button>
                    <button onClick={() => onReject(sub.id, rejectReason)}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-2 rounded-xl text-sm">Confirm Reject</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button onClick={() => setShowReject(true)}
                    className="flex-1 border-2 border-red-200 hover:border-red-400 text-red-600 font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-1.5">
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                  <button onClick={() => onApprove(sub.id)}
                    data-testid={`approve-sub-${sub.id}`}
                    className="flex-2 flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-1.5">
                    <CheckCircle className="w-4 h-4" /> Approve & Activate
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CustomerSubscriptionsAdmin() {
  const { token } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("subscriptions"); // subscriptions | downloads
  const [detail, setDetail] = useState(null);

  // Download analytics state
  const [dlLoading, setDlLoading] = useState(false);
  const [dlData, setDlData] = useState({ records: [], summary: {}, top_products: [] });
  const [dlDateRange, setDlDateRange] = useState("30d");
  const [dlCustomer, setDlCustomer] = useState("");
  const [dlProduct, setDlProduct] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const subRes = await axios.get(`${API}/api/admin/customer-subscriptions`, { headers: { Authorization: `Bearer ${token}` } });
      setSubs(subRes.data);
    } catch { toast.error("Failed to load subscriptions"); }
    finally { setLoading(false); }
  }, [token]);

  const loadAnalytics = useCallback(async () => {
    setDlLoading(true);
    try {
      const params = new URLSearchParams({ date_range: dlDateRange });
      if (dlCustomer) params.set("customer_email", dlCustomer);
      if (dlProduct) params.set("product_search", dlProduct);
      const r = await axios.get(`${API}/api/admin/download-analytics?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setDlData(r.data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setDlLoading(false); }
  }, [token, dlDateRange, dlCustomer, dlProduct]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "downloads") loadAnalytics(); }, [view, loadAnalytics]);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/api/admin/customer-subscriptions/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Subscription activated!");
      setDetail(null);
      load();
    } catch { toast.error("Failed to approve"); }
  };

  const reject = async (id, reason) => {
    try {
      await axios.post(`${API}/api/admin/customer-subscriptions/${id}/reject`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Subscription rejected");
      setDetail(null);
      load();
    } catch { toast.error("Failed to reject"); }
  };

  const filtered = filter === "all" ? subs : subs.filter(s => s.status === filter);
  const pending  = subs.filter(s => s.status === "pending").length;

  const kpis = [
    { label: "Total Subscriptions", value: subs.length,                                      icon: Crown,       color: "bg-orange-500" },
    { label: "Pending Approval",    value: pending,                                           icon: Clock,       color: "bg-yellow-500" },
    { label: "Active Plans",        value: subs.filter(s=>s.status==="active").length,        icon: CheckCircle, color: "bg-green-500" },
    { label: "Total Downloads",     value: dlData.summary?.total_downloads ?? "—",            icon: Download,    color: "bg-blue-500" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {detail && (
        <SubDetailModal sub={detail} onClose={() => setDetail(null)} onApprove={approve} onReject={reject} />
      )}

      <div className="bg-white border-b border-slate-100 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Customer Subscriptions</h1>
            <p className="text-xs text-slate-400">Approve enrollments · view download history</p>
          </div>
          {pending > 0 && (
            <span className="bg-yellow-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">{pending} pending</span>
          )}
        </div>
        <button onClick={load} disabled={loading}
          className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors">
          <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="p-6 space-y-5">
        {/* KPI cards */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {kpis.map(k => (
            <div key={k.label} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.color}`}>
                <k.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xl font-black text-slate-800">{k.value}</p>
                <p className="text-xs text-slate-400">{k.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* View tabs */}
        <div className="flex gap-2">
          {[["subscriptions","Subscriptions"],["downloads","Download History"]].map(([v,l]) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${view===v ? "bg-orange-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-orange-300"}`}>
              {l}
            </button>
          ))}
        </div>

        {/* ── Subscriptions view ── */}
        {view === "subscriptions" && (
          <div className="space-y-3">
            {/* Status filter */}
            <div className="flex gap-2 flex-wrap">
              {["all","pending","active","expired","rejected"].map(s => (
                <button key={s} onClick={() => setFilter(s)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-colors ${filter===s ? "bg-orange-500 text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-orange-300"}`}>
                  {s} {s !== "all" && `(${subs.filter(x=>x.status===s).length})`}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 text-orange-500 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl flex items-center justify-center h-32 text-sm text-slate-400">
                No subscriptions
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-slate-50 text-[11px] font-bold text-slate-400 uppercase">
                  <span className="col-span-3">Customer</span>
                  <span className="col-span-2">Plan</span>
                  <span className="col-span-2">Status</span>
                  <span className="col-span-2">Applied</span>
                  <span className="col-span-2">Expires</span>
                  <span className="col-span-1 text-center">Action</span>
                </div>
                {filtered.map(sub => {
                  const cfg = STATUS_CFG[sub.status] || STATUS_CFG.pending;
                  return (
                    <div key={sub.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-t border-slate-50 hover:bg-orange-50/30 transition-colors">
                      <div className="col-span-3 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{sub.customer_name || "—"}</p>
                        <p className="text-xs text-slate-400 truncate">{sub.customer_email}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm font-semibold text-slate-700 truncate">{sub.plan_name}</p>
                        <p className="text-xs text-slate-400 capitalize">{sub.payment_method?.replace("_"," ")}</p>
                      </div>
                      <div className="col-span-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${cfg.cls}`}>{cfg.label}</span>
                      </div>
                      <div className="col-span-2 text-xs text-slate-500">{fmtDate(sub.created_at)}</div>
                      <div className="col-span-2 text-xs text-slate-500">{fmtDate(sub.end_date)}</div>
                      <div className="col-span-1 flex justify-center">
                        <button onClick={() => setDetail(sub)}
                          className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-orange-600">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Downloads analytics view ── */}
        {view === "downloads" && (
          <div className="space-y-5">
            {/* Filters row */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center gap-3">
              <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
              {/* Date range */}
              <div className="flex gap-1">
                {[["7d","7 days"],["30d","30 days"],["90d","90 days"],["1y","1 year"],["all","All time"]].map(([v,l]) => (
                  <button key={v} onClick={() => setDlDateRange(v)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${dlDateRange===v ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {/* Customer search */}
              <input type="text" placeholder="Filter by customer email" value={dlCustomer}
                onChange={e => setDlCustomer(e.target.value)}
                className="border border-slate-200 rounded-lg text-xs px-3 py-1.5 flex-1 min-w-[160px] focus:outline-none focus:border-orange-400" />
              {/* Product search */}
              <input type="text" placeholder="Filter by product name or SKU" value={dlProduct}
                onChange={e => setDlProduct(e.target.value)}
                className="border border-slate-200 rounded-lg text-xs px-3 py-1.5 flex-1 min-w-[160px] focus:outline-none focus:border-orange-400" />
              <button onClick={loadAnalytics} disabled={dlLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-60">
                <RefreshCw className={`w-3 h-3 ${dlLoading ? "animate-spin" : ""}`} /> Apply
              </button>
            </div>

            {/* Summary KPI cards */}
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
              {[
                { label: "Total Downloads",    value: dlData.summary?.total_downloads ?? 0,    color: "bg-orange-500" },
                { label: "Unique Customers",   value: dlData.summary?.unique_customers ?? 0,   color: "bg-blue-500" },
                { label: "Unique Products",    value: dlData.summary?.unique_products ?? 0,    color: "bg-green-500" },
                { label: "Filtered Records",   value: dlData.summary?.record_count ?? 0,       color: "bg-purple-500" },
              ].map(k => (
                <div key={k.label} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex items-center gap-3">
                  <div className={`w-3 h-10 rounded-full ${k.color}`} />
                  <div>
                    <p className="text-2xl font-black text-slate-800">{k.value}</p>
                    <p className="text-xs text-slate-400">{k.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Top products mini-chart */}
            {dlData.top_products?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <p className="text-sm font-bold text-slate-700 mb-4">Top Downloaded Products</p>
                <div className="space-y-3">
                  {dlData.top_products.map((p, i) => {
                    const maxDl = dlData.top_products[0]?.downloads || 1;
                    const pct = Math.round((p.downloads / maxDl) * 100);
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-slate-700 truncate">{p.name}</span>
                            <span className="text-xs text-slate-500 ml-2 flex-shrink-0">{p.downloads} dl</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Records table */}
            {dlLoading ? (
              <div className="flex items-center justify-center h-32 bg-white border border-slate-200 rounded-2xl">
                <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
              </div>
            ) : dlData.records.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl flex items-center justify-center h-32 text-sm text-slate-400">
                No download records match the current filters
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-slate-50 text-[11px] font-bold text-slate-400 uppercase">
                  <span className="col-span-3">Customer</span>
                  <span className="col-span-4">Product</span>
                  <span className="col-span-2">Added</span>
                  <span className="col-span-2">Last Download</span>
                  <span className="col-span-1 text-center">DLs</span>
                </div>
                {dlData.records.map((d, i) => (
                  <div key={d.id || i} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-t border-slate-50 hover:bg-slate-50/60 transition-colors">
                    <p className="col-span-3 text-xs text-slate-600 truncate">{d.customer_email}</p>
                    <div className="col-span-4 flex items-center gap-2 min-w-0">
                      {d.product_image && <img src={d.product_image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-700 truncate">{d.product_title || d.product_id}</p>
                        {d.product_sku && <p className="text-[10px] text-slate-400">{d.product_sku}</p>}
                      </div>
                    </div>
                    <p className="col-span-2 text-xs text-slate-500">{fmtDate(d.first_added_at)}</p>
                    <p className="col-span-2 text-xs text-slate-500">{fmtDate(d.last_downloaded_at) || "Never"}</p>
                    <p className="col-span-1 text-center text-xs font-bold text-orange-600">{d.download_count || 0}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
